/**
 * The box the day form stands in — the 574px side panel, or the 80% modal — and the library drawer
 * that slides over either of them.
 *
 * ── In this slice since 2 October 2026 ───────────────────────────────────────────────────────
 * It was `pages/plan-builder`'s. The Buddy page mounts the same panel over a draft (owner: the
 * panel the app already has, never a second one), and a page may not import a page, so the box
 * moved down beside the form it holds, with the dialog root around it (`DayPanelDialog`). The
 * catalogue is drawn by a RENDER SLOT (`renderLibrary`), because the rail is another feature's
 * (`exercise-library-rail`) and a feature may not import a feature; every prop it is handed is wired
 * here, so a page passes the form and `ExerciseRail` and nothing else. `DayDock` in the notes below
 * is the builder's composition of this box and the form.
 *
 * ── ONE component, because a host switch must not throw the trainer's work away ─────────────
 * There were two, `BuilderDayPanel` and `BuilderDayModal`, mounted at one tree position by a
 * ternary. React sees two different component TYPES there, so pressing maximise unmounted the whole
 * subtree and mounted a fresh one: the `form` went, and with it the four pieces of state
 * `useDayPanelChrome` owns — the fold defaults, the per-card overrides, whether the superset mode
 * was on and which cards were ticked — plus the body's scroll offset. A trainer who ticked two
 * cards for a superset and then asked for more room got an empty selection and a closed bar.
 * `useMountAfterSlide` restarted too, so the 488-row catalogue was rendered a second time.
 *
 * The two files were also a near-copy of each other: the same ref, the same `useMountAfterSlide`,
 * the same `hasRail` OR, the same `behind`/`inert` handling, the same `initialFocus`/`finalFocus`,
 * both live regions, the same scrim button and the same drawer with the same eight `ExerciseRail`
 * props — and their stylesheets' `.form`, `.libraryScrim`, `.drawer` and `.drawerClosed` blocks were
 * byte-identical while the modal's header claimed the two sheets shared nothing.
 *
 * So the box is a PROP. It picks the root's class, whether a `Dialog.Backdrop` is drawn, and whether
 * the week's auto-scroll gets a mark to measure. Nothing else in this file reads it.
 *
 * ── It is the BOX; the form inside it arrives as a slot ─────────────────────────────────────
 * `DayDock` builds one `DayForm` element and hands it over, so this component never learns what a
 * day form needs. Its interface is the dock's state, the host, and that slot.
 *
 * It IS the popup rather than something rendered inside one: the drawer and its scrim are
 * `position: absolute` against this box, and the `inert` that takes the form out of the tab order is
 * one attribute on one child. `Dialog.Popup` reads its root from context, which `DayPanelDialog`
 * provides; the page stays the one that decides which host the URL asks for.
 *
 * ── The head and the load strip are NOT here, and that is a coordination fact ────────────────
 * The 07.09 programator handoff redraws the head — a 26x24 arrow pair, a
 * "PONIEDZIAŁEK · DZIEŃ 1 Z 4" caption, a 17px title and a strip of 66px muscle bars beside
 * "5 ćwiczeń · 16 serii · ~62 min". Every one of those is drawn by `DayPanelHead` inside
 * `features/plan-day-programming`, in every host. Drawing a second copy here would put two `h2`s
 * carrying one workout's name and two "Zamknij programowanie dnia" buttons in one box, and six
 * existing e2e assertions resolve exactly those by role and name.
 *
 * ── The library is a DRAWER in both boxes, not a second host ────────────────────────────────
 * Pressing "+ Ćwiczenie" in the panel used to switch `?dock=` to `full`, which took the week off
 * screen to fetch a movement. The handoff slides a 320px drawer over the day instead and leaves it
 * exactly where it was; `useDayDock.requestLibrary` is where the two answers parted.
 *
 * The drawer covers the whole host, as the mock draws it, so everything behind it is `inert` while
 * it is out — the covered head and the covered form leave the tab order and the accessibility tree
 * together, rather than staying reachable behind a surface nobody can see them through. Escape
 * closes the DRAWER first and stops there; the next Escape closes the dock, because by then there is
 * nothing over it. That interception is `DayPanelDialog`'s `onOpenChange` and NOT a React
 * `onKeyDown` — see `pages/plan-builder`'s `DayDock` for why the handler this component used to carry
 * never fired.
 *
 * ── The rail stays mounted once it has arrived, and it does NOT arrive with the host ────────
 * Closed, the drawer is `inert` and `pointer-events: none`, so a mounted rail is reachable by no
 * path at all — which is why unmounting it on every close would only cost an empty box sliding back
 * out on the way home. It stays.
 *
 * What matters is WHEN it first arrives. The rail renders the whole catalogue with no cap and no
 * virtualization — 488 rows, and 5231 nodes in the panel against the ~250 the day itself needs — and
 * it did that in the SAME commit that mounts the popup. The dock's press was therefore one
 * 220–320 ms task, and the entrance could not begin until it ended, which is the delay the owner
 * reported as "STRASZNIE długo się odpala ten boczny panel". So the rail waits for the host's
 * entrance to END. Asking for the library BEFORE that mounts it on the press instead — `hasRail` is
 * the OR — so the drawer is never opened onto an empty box and no test has to wait for an animation.
 * `useMountAfterSlide` carries the measurement and why an idle callback was not enough.
 */

import { Dialog } from '@base-ui/react/dialog';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';

import type { DayDockMode, DayDockState } from '../lib/useDayDock';
import { useMountAfterSlide } from '../lib/useMountAfterSlide';

import styles from './DayPanelHost.module.css';
import modal from './DayPanelModal.module.css';

interface DayPanelHostProps {
  /** Which box the day is drawn in. The ONLY thing that differs between the two. */
  readonly host: DayDockMode;
  readonly dock: DayDockState;
  /** The id the popup is labelled by and sends its initial focus to — the day form's own heading. */
  readonly headingId: string;
  /** The dock's live region. Always mounted, empty when there is nothing to say. */
  readonly announcement: string;
  /**
   * The plan SAVE's live region — rendered in the MODAL only. Empty when there is nothing to say.
   *
   * A second region rather than a second message in the first: the dock announces what the trainer
   * did to the day and this announces what the plan's save did, and one string carrying both means
   * one of them overwriting the other.
   *
   * It exists because the modal takes the top bar's own save region out of the accessibility tree,
   * which is exactly when Cmd/Ctrl+S is the only save a keyboard trainer can reach. The panel does
   * NOT — and the note here said it did, "in both boxes", until 13 September 2026. Measured at that
   * date, with the dock open on a day: under the modal the screen's `<header>` carries
   * `aria-hidden="true"` and its "Zapisz" resolves by role 0 times; under the panel nothing carries
   * `aria-hidden` at all, `#root` carries only Base UI's own `data-base-ui-inert` bookkeeping
   * attribute (not the `inert` attribute, and not an ARIA state), and the same button resolves once.
   * So in the panel the bar's region is live, and a second one here made the app say "Zapisano"
   * twice.
   */
  readonly saveAnnouncement: string;
  /** The day form, built once by the dock and placed in whichever box the URL asks for. */
  readonly form: ReactNode;
  /**
   * The builder's Buddy panel overlays the right edge: the day panel stands to its left, both under
   * the top bar, which keeps „Asystent" in reach (owner, 7 October 2026).
   */
  readonly besideAssistant?: boolean;
  /**
   * Draw the exercise catalogue with these props: the page's `ExerciseRail`, spread. The wiring is
   * this component's, once for every page; the rail is a slot only because it is another feature's.
   * Mounted here once the box has arrived, and slid over the day while it is out.
   */
  readonly renderLibrary: (props: DayPanelLibraryProps) => ReactNode;
}

/** Everything the catalogue in the drawer is told: `ExerciseRail`'s props, as this box drives them. */
export interface DayPanelLibraryProps {
  readonly daySlotToken: string | null;
  readonly role: DayDockState['libraryRole'];
  readonly warmupTargetSlotToken: string | null;
  readonly focusSignal: number;
  readonly announce: (message: string) => void;
  readonly onInserted: (exerciseSlotToken: string) => void;
  readonly overlaysTheDay: true;
  readonly onClose: (() => void) | undefined;
  readonly isShown: boolean;
}

interface DayPanelDialogProps extends DayPanelHostProps {
  /** The panel is non-modal (the page beside it stays usable); the modal box is modal. */
  readonly modal: boolean;
}

/**
 * The dock as a dialog: the root, its one way of being asked to close, and the box.
 *
 * Every page that mounts the dock mounts THIS, so the rule for Escape is written once: while the
 * catalogue lies over the day, Escape puts the catalogue away and stops there; otherwise it closes
 * the dock. A press outside never dismisses. See the header for why the interception is made in
 * `onOpenChange` rather than in a key handler.
 */
export function DayPanelDialog({ modal: isModal, ...host }: DayPanelDialogProps) {
  const { dock } = host;
  return (
    <Dialog.Root
      open={dock.daySlotToken !== null && dock.dock !== null}
      modal={isModal}
      disablePointerDismissal
      onOpenChange={(open) => {
        if (open) return;
        if (dock.isLibraryOpen) {
          dock.closeLibrary();
          return;
        }
        dock.close();
      }}
    >
      <Dialog.Portal>
        <DayPanelHost {...host} />
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DayPanelHost({ host, dock, headingId, announcement, saveAnnouncement, form, renderLibrary, besideAssistant = false }: DayPanelHostProps) {
  const { t } = useTranslation();
  const isModal = host === 'full';
  const isLibraryOpen = dock.isLibraryOpen;
  const hostRef = useRef<HTMLDivElement>(null);
  // The catalogue behind the closed drawer waits for the host to finish arriving — see
  // `useMountAfterSlide`. Asking for the library first mounts it on that press instead, so the
  // drawer is never opened onto an empty box. Latched, and this component survives a host switch,
  // so switching boxes does not render the catalogue a second time.
  const hasRail = useMountAfterSlide(hostRef) || isLibraryOpen;
  // `inert` takes `true | undefined`. `inert={false}` is not the same as omitting it in every
  // renderer, and an attribute that is present is an attribute that is on.
  const behind = isLibraryOpen ? true : undefined;

  return (
    <>
      {/* The modal's dimming layer. The panel has none: the week beside it stays readable, which is
        * the whole reason a trainer keeps the day at 574px. */}
      {isModal && <Dialog.Backdrop className={modal.scrim} />}
      <Dialog.Popup
        ref={hostRef}
        className={isModal ? modal.modal : `${String(styles.panel)} ${besideAssistant ? String(styles.panelBesideAssistant) : ''}`}
        /* `data-day-panel` is what `useRevealOpenDay` measures the obstruction by. An attribute
         * rather than a threaded ref, for the same reason the calendar's overlay carries one: Base
         * UI portals this popup into `<body>`, so there is no parent that could hold a ref, and the
         * question the hook asks is "whatever is lying over the week" rather than "this particular
         * component". The MODAL carries none — it obstructs the week entirely, and there is nothing
         * behind it to scroll a card clear of. */
        data-day-panel={isModal ? undefined : ''}
        aria-labelledby={headingId}
        // The heading, never the DOM's first tabbable element — which here is the ‹ that leaves the
        // day the trainer just opened. `null` falls back to Base UI's default. See `DayDock`.
        initialFocus={() => globalThis.document.getElementById(headingId)}
        // The opener may be unmounted by the time Base UI would restore focus to it; `useDayDock`
        // names the day instead and the card's own button claims focus when it re-mounts.
        finalFocus={false}
      >
        <LiveRegionAnnouncer message={announcement} />
        {/* The save's own words, and only where the bar's region cannot be heard — see the prop. */}
        {isModal && <LiveRegionAnnouncer message={saveAnnouncement} />}

        <div className={styles.form} inert={behind}>
          {form}
        </div>

        {/* The drawer's scrim. A real BUTTON rather than a decorative box: putting the library away
          * needs a pointer path and a keyboard path, and this is both. Rendered only while the
          * drawer is out, so nothing invisible sits in the tab order the rest of the time. Its ink
          * is the box's — see `--scrim-library`.
          *
          * Its NAME says it is the ground behind the catalogue rather than repeating the drawer's
          * own ✕ word for word. The two were "Zamknij bibliotekę ćwiczeń" each until 13 September
          * 2026 — two tabbable controls answering to one name, which is a screen reader offering the
          * same choice twice with nothing to tell them apart. */}
        {isLibraryOpen && (
          <button
            type="button"
            className={styles.libraryScrim}
            aria-label={t('web:planBuilder.day.closeLibrary')}
            onClick={dock.closeLibrary}
          />
        )}

        <div
          className={`${String(styles.drawer)} ${isLibraryOpen ? '' : String(styles.drawerClosed)}`}
          inert={isLibraryOpen ? undefined : true}
        >
          {hasRail &&
            renderLibrary({
              daySlotToken: dock.daySlotToken,
              role: dock.libraryRole,
              warmupTargetSlotToken: dock.libraryWarmupTarget,
              focusSignal: dock.focusSignal,
              announce: dock.announce,
              // The drawer is the one library that stands OVER the day it edits, so a successful
              // add is also the end of the reach: it goes away, and focus follows the card it just
              // made. A press, a `Rozgrzewka` press and the foot's batch all arrive here.
              onInserted: dock.exerciseAdded,
              overlaysTheDay: true,
              // Handed over only while the drawer is OUT: a closed drawer resolves no
              // „Zamknij bibliotekę ćwiczeń" button, as four e2e specs pin.
              onClose: isLibraryOpen ? dock.closeLibrary : undefined,
              // Mounted shut for the warm-up; the search's other-language read waits for it.
              isShown: isLibraryOpen,
            })}
        </div>
      </Dialog.Popup>
    </>
  );
}
